import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { QuickAppointmentContext } from '../../Context';
import { areTimeSlotsEnabled } from '../../helper';
import AvailabilityWindowsCalendar from './AvailabilityWindowsCalendar';
import TimeSlotCalendar from './TimeSlotsCalendar';

const CompatibleDatesCalendar = ({ record, serviceVehicleList }) => {
  const {
    rruleCombined,
    setDatesToCreate,
    setUnavailableRecurringDates,
    setRecurringDatesWithConflicts,
    isLoadingAdditionalDates,
    selectedTimeSlot,
    availableDatesToShow,
  } = useContext(QuickAppointmentContext);

  const isUsingTimeSlots = areTimeSlotsEnabled();

  const [selectedDate, setSelectedDate] = useState(null);

  const shouldDisableDate = (date) => {
    return !(
      availableDatesToShow?.length > 0 &&
      availableDatesToShow
        .map((d) => d.date)
        .includes(moment(date).format('YYYY-MM-DD'))
    );
  };

  // Set repeat appointment dates, unavailable repeat dates, and find repeat dates with conflicts
  useEffect(() => {
    if (!availableDatesToShow) {
      setDatesToCreate(rruleCombined.all());
    } else {
      const rruleDates = rruleCombined
        .all()
        .map((d) => moment(d).format('YYYY-MM-DD'));
      const serviceVehicleDateSet = new Set(
        availableDatesToShow?.map((d) => d.date),
      );
      const bookableDates = rruleDates
        .filter((d) => serviceVehicleDateSet.has(d))
        .map((d) => new Date(`${d}T00:00:00`));
      const unavailableDates = rruleDates
        .filter((d) => !serviceVehicleDateSet.has(d))
        .map((d) => new Date(`${d}T00:00:00`));

      const datesWithConflicts = availableDatesToShow
        ?.filter((d) => {
          const dateInPattern = rruleCombined
            .all()
            .map((rruleDate) => moment(rruleDate).format('YYYY-MM-DD'))
            .includes(d.date);
          const hasDateConflict = !d.is_available && dateInPattern;
          if (
            !hasDateConflict &&
            dateInPattern &&
            isUsingTimeSlots &&
            selectedTimeSlot
          ) {
            const hasTimeConflict = !d?.available_start_times
              .map((t) => moment(t).format('h:mm a'))
              .includes(selectedTimeSlot);
            if (hasTimeConflict) {
              return true;
            }
          }
          return hasDateConflict;
        })
        .map((d) => {
          if (
            isUsingTimeSlots &&
            selectedTimeSlot &&
            !d?.available_start_times
              .map((t) => moment(t).format('h:mm a'))
              .includes(selectedTimeSlot)
          ) {
            return {
              date: d.date,
              short_reason: `${selectedTimeSlot} Time Not Free`,
            };
          }
          return { date: d.date, short_reason: d.unavailable_reason_short };
        });
      setDatesToCreate(bookableDates);
      setUnavailableRecurringDates(unavailableDates);
      setRecurringDatesWithConflicts(datesWithConflicts);
    }
  }, [
    rruleCombined,
    availableDatesToShow,
    isLoadingAdditionalDates,
    selectedTimeSlot,
  ]);

  return isUsingTimeSlots ? (
    <TimeSlotCalendar
      record={record}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      serviceVehicleList={serviceVehicleList}
    />
  ) : (
    <AvailabilityWindowsCalendar
      record={record}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      shouldDisableDate={shouldDisableDate}
    />
  );
};

export default CompatibleDatesCalendar;
